import { Entitlements, PremiumFeatures } from '@trello/entitlements';
import { intl } from '@trello/i18n';
import { useSharedState } from '@trello/shared-state';
import {
  getWorkspaceCustomTableViewUrl,
  getWorkspaceDefaultCustomCalendarViewUrl,
} from '@trello/urls';

import { canSeeWorkspaceViewEntrypoints } from 'app/src/components/ViewsGenerics/canSeeWorkspaceViewEntrypoints';
import { workspaceViewNavigationState } from './workspaceViewNavigationState';
import { useWorkspaceViewsEligibilityQuery } from './WorkspaceViewsEligibilityQuery.generated';
import type { ViewItem } from './WorkspaceViewsList';
import { useWorkspaceViewsQuery } from './WorkspaceViewsQuery.generated';

export const getDefaultViewsForCurrentWorkspace = ({
  organization,
}: {
  organization: {
    premiumFeatures: string[];
    offering: string;
    name: string;
  };
}): ViewItem[] => {
  const views: ViewItem[] = [];
  const hasWorkspaceViewsFeature = organization?.premiumFeatures?.includes(
    PremiumFeatures.workspaceViews,
  );
  const isEnterprise = Entitlements.isEnterprise(organization.offering);

  // Only teams that won't have this are legacy BC
  if (canSeeWorkspaceViewEntrypoints(hasWorkspaceViewsFeature, isEnterprise)) {
    views.push({
      title: intl.formatMessage({
        id: 'templates.workspace_navigation.table',
        defaultMessage: 'Table',
        description: 'Title for the table view',
      }),
      linkName: 'teamTableLink',
      href: getWorkspaceCustomTableViewUrl(organization.name, '?populate='),
      iconName: 'table',
      viewType: 'table',
    });
    views.push({
      title: intl.formatMessage({
        id: 'templates.workspace_navigation.calendar',
        defaultMessage: 'Calendar',
        description: 'Title for the calendar view',
      }),
      linkName: 'workspaceCalendarLink',
      href: getWorkspaceDefaultCustomCalendarViewUrl(
        organization.name,
        '?populate=',
      ),
      iconName: 'calendar',
      viewType: 'calendar',
    });
  }

  return views;
};

export function useWorkspaceViews({
  workspaceId,
  userRelationshipToWorkspace,
}: {
  workspaceId: string;
  skip?: boolean;
  userRelationshipToWorkspace: string;
}) {
  const {
    error: workspaceViewsEligibilityError,
    data: workspaceViewsEligibilityData,
    loading: workspaceViewsEligibilityLoading,
  } = useWorkspaceViewsEligibilityQuery({
    // workspaceId is null for personal workspaces
    variables: {
      orgId: workspaceId,
    },
    skip: !workspaceId,
    waitOn: ['MemberHeader', 'MemberBoards'],
  });

  const hasWorkspaceViewsFeature =
    workspaceViewsEligibilityData?.organization?.premiumFeatures?.includes(
      PremiumFeatures.workspaceViews,
    ) ?? false;

  const skipWorkspaceViewsQuery =
    workspaceViewsEligibilityLoading ||
    // Skip if the workspace doesn't have workspace views
    !hasWorkspaceViewsFeature ||
    // Skip is the member is not a member of the workspace
    userRelationshipToWorkspace !== 'MEMBER';

  const [workspaceViewState] = useSharedState(workspaceViewNavigationState);

  const {
    data: workspaceViewsData,
    loading: workspaceViewsLoading,
    error: workspaceViewsError,
  } = useWorkspaceViewsQuery({
    variables: {
      orgId: workspaceId,
      organizationViewsFilter: workspaceViewState['filter'],
    },
    skip: skipWorkspaceViewsQuery,
    waitOn: ['None'],
  });

  if (
    workspaceViewsError ||
    workspaceViewsLoading ||
    workspaceViewsEligibilityLoading ||
    workspaceViewsEligibilityError ||
    !workspaceViewsEligibilityData?.organization
  ) {
    return {
      hasWorkspaceViewsFeature,
      views: undefined,
      organizationViews: undefined,
      error: workspaceViewsEligibilityError || workspaceViewsError,
      loading: workspaceViewsEligibilityLoading || workspaceViewsLoading,
    };
  }

  return {
    hasWorkspaceViewsFeature,
    views: getDefaultViewsForCurrentWorkspace({
      organization: {
        name: workspaceViewsEligibilityData?.organization?.name,
        offering: workspaceViewsEligibilityData?.organization?.offering,
        premiumFeatures:
          workspaceViewsEligibilityData?.organization?.premiumFeatures,
      },
    }),
    organizationViews: workspaceViewsData?.organization?.organizationViews,
    error: undefined,
    loading: false,
  };
}
